<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveCannedReply">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Edit canned reply') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <button
                            :disabled="!cannedReply.author"
                            class="btn btn-red shadow-sm rounded"
                            type="button"
                            @click="deleteCannedReplyModal = true"
                        >
                            {{ $t('Delete canned reply') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Canned reply details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Canned reply details and settings') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}</label>
                                    <div class="mt-1 relative rounded shadow-sm">
                                        <input
                                            id="name"
                                            v-model="cannedReply.name"
                                            :placeholder="$t('Name')"
                                            :readonly="!cannedReply.author"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="shared">{{ $t('Shared') }}</label>
                                    <input-switch
                                        id="shared"
                                        v-model="cannedReply.shared"
                                        :disabled-label="$t('The canned reply is not shared')"
                                        :enabled-label="$t('The canned reply is shared')"
                                        :readonly="!cannedReply.author"
                                    ></input-switch>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        {{ $t('A shared saved answer, allows other users to use it') }}.
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="body">{{ $t('Body') }}</label>
                                    <input-wysiwyg
                                        id="body"
                                        v-model="cannedReply.body"
                                        :readonly="!cannedReply.author"
                                    ></input-wysiwyg>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="stage-footer-surface text-right px-4 py-3 sm:px-6">
                        <div class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded mr-4"
                                :to="cannedReplyListPath"
                            >
                                {{ $t('Close') }}
                            </router-link>
                            <button
                                :disabled="!cannedReply.author"
                                class="btn btn-gray shadow-sm rounded"
                                type="submit"
                            >
                                {{ $t('Edit canned reply') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <confirm-dialog :open="deleteCannedReplyModal && Boolean(cannedReply.author)" :title="$t('Delete canned reply').toString()" :text="`${$t('Are you sure you want to delete the canned reply?')} ${$t('All data will be permanently removed')}. ${$t('All related data will be deleted')}. ${$t('This action cannot be undone')}.`" :confirm-label="$t('Delete canned reply').toString()" confirm-variant="danger" @close="deleteCannedReplyModal = false" @confirm="deleteCannedReply" />
    </main>
</template>

<script>
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {resolveReturnToPath} from "@/utils/list-route-state";

export default {
    components: {ConfirmDialog},
    name: "edit",
    metaInfo() {
        return {
            title: this.$i18n.t('Edit canned reply')
        }
    },
    data() {
        return {
            loading: false,
            deleteCannedReplyModal: false,
            cannedReply: {
                name: null,
                shared: true,
                body: '',
                author: false,
            },
        }
    },
    mounted() {
        this.getCannedReply();
    },
    computed: {
        cannedReplyListPath() {
            const isSettingsRoute = String(this.$route && this.$route.path ? this.$route.path : '').startsWith('/workspace/admin/settings/canned-replies');
            const fallbackPath = isSettingsRoute ? '/workspace/admin/settings/canned-replies' : '/workspace/canned-replies';
            return resolveReturnToPath(this.$route.query || {}, fallbackPath);
        },
    },
    methods: {
        saveCannedReply() {
            const self = this;
            self.loading = true;
            axios.put('api/workspace/canned-replies/' + self.$route.params.id, self.cannedReply).then(function () {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        },
        getCannedReply() {
            const self = this;
            self.loading = true;
            axios.get('api/workspace/canned-replies/' + self.$route.params.id).then(function (response) {
                self.cannedReply = response.data;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        deleteCannedReply() {
            const self = this;
            axios.delete('api/workspace/canned-replies/' + self.$route.params.id).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.push(self.cannedReplyListPath);
            }).catch(function () {
                self.deleteCannedReplyModal = false;
            });
        },
    }
}
</script>
